
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSeller } from "../../context/SellerContext";
import useBranch from "../../hooks/useBranch";
import {
  Maximize,
  Minimize,
  Wifi,
  ArrowLeft,
  LogOut,
  User,
  ChevronDown,
  Building2,
  Store,
} from 'lucide-react';

export default function Header({
  onBack,
  branchName: propBranchName,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { branchName: hookBranchName } = useBranch();

  const displayName = propBranchName || hookBranchName;
  const { selectedSeller } = useSeller();
  const [isMaximized, setIsMaximized] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [showLogoutMenu, setShowLogoutMenu] = useState(false);

  const handleResetPassword = () => {
    navigate("/profile/reset-password?call=profile");
    setShowLogoutMenu(false);
  };

  useEffect(() => {
    const handleChange = () => setIsMaximized(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showLogoutMenu && !e.target.closest('.user-menu')) {
        setShowLogoutMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLogoutMenu]);

  const toggleMaximize = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) {
      console.warn('Fullscreen toggle failed', e);
    }
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
    setShowLogoutMenu(false);
  }, [logout, navigate]);

  return (
    <header className="bg-[#131921] shadow-lg">
      <div className="px-4 sm:px-6 py-2.5 flex justify-between items-center">
        {/* Left Side */}
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              onClick={onBack}
              className="bg-white/10 hover:bg-white/20 p-2 rounded-lg transition text-white flex items-center"
              title="Back"
            >
              <ArrowLeft size={18} />
            </button>
          )}

          {/* App Branding */}
          <img
            src="/ecomm-logo.png"
            alt="eComm Notify"
            className="h-9 w-auto cursor-pointer select-none"
            onClick={() => navigate('/sellers')}
          />


          {/* Seller context */}
          {selectedSeller && (
            <>
              <div className="w-px h-5 bg-white/15 mx-1" />
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/10">
                <Store size={13} className="text-[#f08804] flex-shrink-0" />
                <span className="text-[13px] font-medium text-white truncate max-w-[200px]">
                  {selectedSeller.businessName}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-2">
          {/* Online indicator */}
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium ${
              online ? 'text-green-400' : 'text-rose-400'
            }`}
            title={online ? 'Online' : 'Offline'}
          >
            <Wifi size={14} />
            <span>{online ? 'Online' : 'Offline'}</span>
          </div>

          {/* Fullscreen toggle */}
          <button
            onClick={toggleMaximize}
            className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition"
            title={isMaximized ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isMaximized ? <Minimize size={16} /> : <Maximize size={16} />}
          </button>

          {/* User Menu */}
          <div className="relative user-menu">
            <button
              onClick={() => setShowLogoutMenu(!showLogoutMenu)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 hover:border-[#f08804]/50 hover:bg-white/5 text-white transition"
              title={`Logged in as ${user?.name || 'User'}`}
            >
              <div className="w-6 h-6 rounded-full bg-[#f08804] flex items-center justify-center text-[#131921] text-[10px] font-bold">
                {(user?.name || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-[10px] text-white/50 leading-none">Hello,</p>
                <p className="text-xs font-semibold leading-tight">{user?.name || 'User'}</p>
              </div>
              <ChevronDown size={12} className={`text-white/50 transition-transform ${showLogoutMenu ? 'rotate-180' : ''}`} />
            </button>

            {showLogoutMenu && (
              <div className="absolute right-0 mt-2 w-64 rounded-lg overflow-hidden z-50 shadow-xl border border-[#DDD] bg-white">
                {/* User Card */}
                <div className="px-4 py-3 bg-[#232F3E]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#f08804] rounded-full flex items-center justify-center shrink-0">
                      <span className="text-[#131921] font-bold text-sm">
                        {(user?.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">
                        {user?.name || 'User'}
                      </p>
                      <p className="text-[11px] text-[#f08804] capitalize">
                        {user?.userType?.toLowerCase()?.replace('_', ' ') || 'Staff'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Branch Info */}
                {displayName && (
                  <div className="px-4 py-2.5 bg-[#F7F7F7] border-b border-[#eee] flex items-center gap-2">
                    <Building2 size={13} className="text-[#888] flex-shrink-0" />
                    <span className="text-[12px] text-[#555] font-medium truncate">{displayName}</span>
                  </div>
                )}

                {/* Actions */}
                <div>
                  <button
                    onClick={() => handleResetPassword("profile")}
                    className="w-full px-4 py-2.5 text-left hover:bg-[#F3F3F3] transition-colors flex items-center gap-3"
                  >
                    <User size={15} className="text-[#555]" />
                    <span className="text-sm text-[#0F1111]">Reset Password</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2.5 text-left hover:bg-red-50 transition-colors flex items-center gap-3 border-t border-[#eee]"
                  >
                    <LogOut size={15} className="text-red-500" />
                    <span className="text-sm text-red-600 font-medium">Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
